(() => {
  'use strict';

  const GLOBAL_KEY = '__LP_CARTPAGE_CONVERSIONS_V1__';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  const state = {
    instances: new Map(),
    initialized: false,
    init
  };

  window[GLOBAL_KEY] = state;

  class LPCartPage {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || root.id;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.config = this.readConfig();
      this.queue = Promise.resolve();
      this.statusTimer = null;

      this.full = root.querySelector('[data-lpcp-full]');
      this.empty = root.querySelector('[data-lpcp-empty]');
      this.items = root.querySelector('[data-lpcp-items]');
      this.status = root.querySelector('[data-lpcp-status]');
      this.subtotal = root.querySelector('[data-lpcp-subtotal]');
      this.subtotalLabel = root.querySelector('[data-lpcp-subtotal-label]');
      this.discountRow = root.querySelector('[data-lpcp-discount-row]');
      this.discount = root.querySelector('[data-lpcp-discount]');
      this.total = root.querySelector('[data-lpcp-total]');
      this.checkoutTotal = root.querySelector('[data-lpcp-checkout-total]');

      this.onClick = this.onClick.bind(this);
      this.onPageShow = this.onPageShow.bind(this);

      this.bind();
    }

    readConfig() {
      const node = this.root.querySelector('[data-lpcp-config]');

      if (!node) return {};

      try {
        return JSON.parse(node.textContent || '{}');
      } catch (error) {
        console.error('[LP Cartpage] Invalid configuration.', error);
        return {};
      }
    }

    bind() {
      this.root.addEventListener('click', this.onClick, { signal: this.signal });
      window.addEventListener('pageshow', this.onPageShow, { signal: this.signal });
    }

    destroy() {
      this.controller.abort();
      window.clearTimeout(this.statusTimer);
    }

    onPageShow(event) {
      if (event.persisted) {
        this.refreshCart().catch(() => {});
      }
    }

    onClick(event) {
      const change = event.target.closest('[data-lpcp-change]');

      if (change) {
        event.preventDefault();
        const key = change.dataset.key;
        const quantity = Number(change.dataset.quantity);
        this.changeLine(key, quantity);
        return;
      }

      const remove = event.target.closest('[data-lpcp-remove]');

      if (remove) {
        event.preventDefault();
        this.changeLine(remove.dataset.key, 0);
        return;
      }

      const add = event.target.closest('[data-lpcp-add]');

      if (add) {
        event.preventDefault();
        this.addVariant(add, Number(add.dataset.variantId));
      }
    }

    enqueue(task) {
      const run = this.queue.then(task, task);
      this.queue = run.catch(() => {});
      return run;
    }

    changeLine(key, quantity) {
      if (!key || !Number.isFinite(quantity) || quantity < 0) return;

      const line = this.root.querySelector(`[data-lpcp-line][data-key="${this.escapeSelector(key)}"]`);
      line?.classList.add('is-busy');

      this.setStatus(this.config.loadingText || 'Updating your cart…');

      return this.enqueue(async () => {
        try {
          const response = await fetch(this.route('cart/change.js'), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ id: key, quantity })
          });

          const cart = await this.readResponse(response);

          if (!response.ok) {
            throw new Error(cart.description || cart.message || this.config.errorText);
          }

          this.render(cart);
          this.syncHeader(cart);
          this.setStatus(
            quantity === 0
              ? this.config.removedText || 'Item removed.'
              : this.config.updatedText || 'Cart updated.'
          );

          return cart;
        } catch (error) {
          line?.classList.remove('is-busy');
          this.setStatus(error.message || this.config.errorText || 'Something went wrong. Please try again.', true);
          throw error;
        }
      });
    }

    addVariant(button, variantId) {
      if (!Number.isFinite(variantId) || variantId <= 0 || button.disabled) return;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      this.setStatus(this.config.loadingText || 'Updating your cart…');

      return this.enqueue(async () => {
        try {
          const response = await fetch(this.route('cart/add.js'), {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });

          const payload = await this.readResponse(response);

          if (!response.ok) {
            throw new Error(payload.description || payload.message || this.config.errorText);
          }

          const cart = await this.fetchCart();
          this.render(cart);
          this.syncHeader(cart);
          this.setStatus(this.config.addedText || 'Added to your cart.');

          return cart;
        } catch (error) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          this.setStatus(error.message || this.config.errorText || 'Something went wrong. Please try again.', true);
          throw error;
        }
      });
    }

    async refreshCart() {
      const cart = await this.fetchCart();
      this.render(cart);
      this.syncHeader(cart);
      return cart;
    }

    async fetchCart() {
      const response = await fetch(`${this.route('cart.js')}?_lpcp=${Date.now()}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store'
      });

      const cart = await this.readResponse(response);

      if (!response.ok) {
        throw new Error(cart.description || cart.message || this.config.errorText);
      }

      return cart;
    }

    route(path) {
      const root = this.config.rootUrl || window.Shopify?.routes?.root || '/';
      return `${root}${path}`.replace(/([^:]\/)\/+/g, '$1');
    }

    async readResponse(response) {
      const type = response.headers.get('content-type') || '';

      if (type.includes('application/json')) {
        return response.json();
      }

      const text = await response.text();

      try {
        return JSON.parse(text);
      } catch (error) {
        return { description: text };
      }
    }

    render(cart) {
      const empty = !cart.items || cart.items.length === 0;

      if (this.full) this.full.hidden = empty;
      if (this.empty) this.empty.hidden = !empty;

      if (empty) {
        this.items?.replaceChildren();
        this.updateTotals(cart);
        return;
      }

      if (this.items) {
        const fragment = document.createDocumentFragment();

        cart.items.forEach((item) => {
          fragment.appendChild(this.createItem(item));
        });

        this.items.replaceChildren(fragment);
      }

      this.updateTotals(cart);
      this.updateUpsells(cart);
    }

    updateTotals(cart) {
      const count = Number(cart.item_count || 0);
      const itemWord = count === 1
        ? this.config.itemSingular || 'item'
        : this.config.itemPlural || 'items';

      if (this.subtotalLabel) {
        this.subtotalLabel.textContent = `${this.config.subtotalLabel || 'Subtotal'} (${count} ${itemWord})`;
      }

      if (this.subtotal) {
        this.subtotal.textContent = this.formatMoney(cart.items_subtotal_price ?? cart.total_price ?? 0);
      }

      const totalDiscount = Number(cart.total_discount || 0);

      if (this.discountRow) {
        this.discountRow.hidden = totalDiscount <= 0;
      }

      if (this.discount) {
        this.discount.textContent = `−${this.formatMoney(totalDiscount)}`;
      }

      const total = this.formatMoney(cart.total_price || 0);

      if (this.total) this.total.textContent = total;
      if (this.checkoutTotal) this.checkoutTotal.textContent = total;
    }

    createItem(item) {
      const article = document.createElement('article');
      article.className = 'lpcp__item';
      article.dataset.lpcpLine = '';
      article.dataset.key = item.key;

      const media = document.createElement('a');
      media.className = 'lpcp__item-media';
      media.href = item.url || this.config.cartUrl || '/cart';
      media.setAttribute('aria-label', item.product_title || item.title || 'Cart item');

      const imageUrl = item.featured_image?.url || item.image;

      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'lpcp__item-image';
        image.src = this.resizeImage(imageUrl, 260);
        image.alt = item.featured_image?.alt || item.product_title || '';
        image.loading = 'lazy';
        image.width = 112;
        image.height = 138;
        media.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'lpcp__item-placeholder';
        placeholder.setAttribute('aria-hidden', 'true');
        media.appendChild(placeholder);
      }

      const info = document.createElement('div');
      info.className = 'lpcp__item-info';

      const title = document.createElement('a');
      title.className = 'lpcp__item-title';
      title.href = item.url || this.config.cartUrl || '/cart';
      title.textContent = item.product_title || item.title || 'Product';
      info.appendChild(title);

      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variant = document.createElement('p');
        variant.className = 'lpcp__item-variant';
        variant.textContent = item.variant_title;
        info.appendChild(variant);
      }

      Object.entries(item.properties || {}).forEach(([name, value]) => {
        if (!value || name.startsWith('_')) return;

        const property = document.createElement('p');
        property.className = 'lpcp__item-property';

        const label = document.createElement('span');
        label.textContent = `${name}: `;
        property.appendChild(label);
        property.append(document.createTextNode(String(value)));
        info.appendChild(property);
      });

      const delivery = document.createElement('p');
      delivery.className = 'lpcp__delivery-line';
      delivery.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M12 3v12m0 0 5-5m-5 5-5-5M5 20h14"/></svg>';
      delivery.append(document.createTextNode(this.config.deliveryText || 'Instant digital delivery'));
      info.appendChild(delivery);

      const price = document.createElement('div');
      price.className = 'lpcp__item-price';
      price.textContent = this.formatMoney(item.final_line_price ?? item.line_price ?? 0);

      const actions = document.createElement('div');
      actions.className = 'lpcp__item-actions';

      const quantity = document.createElement('div');
      quantity.className = 'lpcp__quantity';
      quantity.setAttribute('aria-label', `Quantity for ${item.product_title || item.title || 'item'}`);

      quantity.append(
        this.quantityButton('−', this.config.decreaseLabel || 'Decrease quantity', item.key, Math.max(0, item.quantity - 1)),
        this.quantityNumber(item.quantity),
        this.quantityButton('+', this.config.increaseLabel || 'Increase quantity', item.key, item.quantity + 1)
      );

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'lpcp__remove';
      remove.dataset.lpcpRemove = '';
      remove.dataset.key = item.key;
      remove.setAttribute('aria-label', `${this.config.removeLabel || 'Remove'} ${item.product_title || item.title || 'item'}`);
      remove.innerHTML = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M9 7V4h6v3M7 7l1 14h8l1-14M10 11v6M14 11v6"/></svg>';

      const removeText = document.createElement('span');
      removeText.textContent = this.config.removeLabel || 'Remove';
      remove.appendChild(removeText);

      actions.append(quantity, remove);
      article.append(media, info, price, actions);

      return article;
    }

    quantityButton(text, label, key, quantity) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = text;
      button.dataset.lpcpChange = '';
      button.dataset.key = key;
      button.dataset.quantity = String(quantity);
      button.setAttribute('aria-label', label);
      return button;
    }

    quantityNumber(value) {
      const span = document.createElement('span');
      span.textContent = String(value);
      span.setAttribute('aria-live', 'polite');
      return span;
    }

    updateUpsells(cart) {
      const productIds = new Set((cart.items || []).map((item) => String(item.product_id)));

      this.root.querySelectorAll('[data-lpcp-upsell]').forEach((card) => {
        const button = card.querySelector('[data-lpcp-add]');
        if (!button) return;

        const inCart = productIds.has(String(card.dataset.productId));
        button.disabled = inCart;
        button.removeAttribute('aria-busy');

        const label = button.querySelector('span');
        if (label) {
          label.textContent = inCart
            ? this.config.inCartLabel || 'In cart'
            : this.config.addLabel || 'Add';
        }
      });
    }

    syncHeader(cart) {
      document.querySelectorAll('[data-lpch-count]').forEach((node) => {
        node.textContent = String(cart.item_count || 0);
        node.classList.toggle('is-hidden', Number(cart.item_count || 0) === 0);
      });

      document.dispatchEvent(new CustomEvent('lp:cart:refresh', {
        detail: {
          cart,
          source: 'lp-cartpage-conversions-v1'
        }
      }));
    }

    resizeImage(value, width) {
      try {
        const url = new URL(value, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.toString();
      } catch (error) {
        return value;
      }
    }

    formatMoney(cents) {
      const currency = this.config.currency || 'GBP';
      const locale = this.config.locale || document.documentElement.lang || 'en-GB';

      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(Number(cents || 0) / 100);
      } catch (error) {
        return `${currency} ${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    setStatus(message, isError = false) {
      if (!this.status) return;

      window.clearTimeout(this.statusTimer);
      this.status.textContent = message || '';
      this.status.classList.toggle('is-error', Boolean(isError));

      if (message) {
        this.statusTimer = window.setTimeout(() => {
          if (this.status) this.status.textContent = '';
        }, isError ? 6000 : 2600);
      }
    }

    escapeSelector(value) {
      if (window.CSS?.escape) return window.CSS.escape(value);
      return String(value).replace(/["\\]/g, '\\$&');
    }
  }

  function init(scope = document) {
    scope.querySelectorAll?.('[data-lpcp]').forEach((root) => {
      const sectionId = root.dataset.sectionId || root.id;
      const existing = state.instances.get(sectionId);

      if (existing?.root === root) return;
      existing?.destroy();

      state.instances.set(sectionId, new LPCartPage(root));
    });
  }

  function boot() {
    init(document);

    if (state.initialized) return;
    state.initialized = true;

    document.addEventListener('shopify:section:load', (event) => init(event.target));

    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.querySelector?.('[data-lpcp]') || (event.target.matches?.('[data-lpcp]') ? event.target : null);
      if (!root) return;

      const sectionId = root.dataset.sectionId || root.id;
      state.instances.get(sectionId)?.destroy();
      state.instances.delete(sectionId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
